import {
  Group,
  Stack,
  Text,
  Title,
  Paper,
  ThemeIcon,
  Divider,
  Badge,
  Flex,
} from '@mantine/core';
import visa from '../../assets/card-logos/visa.webp';
import mastercard from '../../assets/card-logos/mastercard.webp';
import amex from '../../assets/card-logos/amex.webp';
import discover from '../../assets/card-logos/discover.webp';
import classes from './TransactionResult.module.css';
import { CheckIcon, CreditCardIcon, IdCardIcon, XIcon } from 'lucide-react';
import type { PaymentMethodType } from '@shared/components/PaymentForm/PaymentMethodSelector';

export type TransactionResult =
  | {
      status: 'success';
      paymentMethod: PaymentMethodType;
      amount: string;
      payFacTransactionId: string;
      paymentInstrument: {
        id: string;
        accountType: string | undefined;
        bankCode: string | undefined;
        lastFour: string | undefined;
        brand: string | undefined;
        cardType: string | undefined;
        expirationMonth: number | undefined;
        expirationYear: number | undefined;
        name: string | undefined;
        maskedAccountNumber: string | undefined;
      };
    }
  | {
      status: 'failed';
      recoverable: boolean;
    }
  | {
      status: 'cancelled';
    };

export const TransactionResultPanel = ({
  opacity,
  summary,
}: {
  opacity: number;
  summary: TransactionResult | undefined;
}) => {
  if (!summary) {
    return null;
  }

  const isHidden = opacity === 0;

  return (
    <div
      className={`transaction-result-panel ${classes.transactionResultPanel}`}
      style={{
        opacity,
        transition: 'opacity 0.3s ease-in-out',
        boxSizing: 'border-box',
        visibility: isHidden ? 'hidden' : 'visible',
      }}
      aria-hidden={isHidden}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '480px',
          animation: 'fadeInUp 0.4s ease-out',
        }}
      >
        <Paper
          shadow='sm'
          radius='md'
          withBorder
          className={
            summary.status === 'success'
              ? classes.successCard
              : summary.status === 'failed'
                ? classes.failedCard
                : classes.cancelledCard
          }
        >
          {summary.status === 'success' && (
            <TransactionCompleted result={summary} />
          )}
          {summary.status === 'cancelled' && <TransactionCancelled />}
          {summary.status === 'failed' && (
            <TransactionFailed recoverable={summary.recoverable} />
          )}
        </Paper>
      </div>
    </div>
  );
};

const TransactionCompleted = ({
  result,
}: {
  result: Extract<TransactionResult, { status: 'success' }>;
}) => {
  const cardLogoSrc = result.paymentInstrument.brand
    ? getCardLogoSrc(result.paymentInstrument.brand)
    : undefined;

  return (
    <>
      <div className={classes.resultHeader}>
        <ThemeIcon
          size={44}
          radius={44}
          color='green'
          className={classes.statusIcon}
        >
          <CheckIcon size={24} strokeWidth={2.5} />
        </ThemeIcon>
        <Title size='h4' className={classes.resultTitle}>
          Payment Complete
        </Title>
      </div>

      <div className={classes.amountSection}>
        <Text size='xl' className={classes.amountValue}>
          {Number(result.amount).toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          })}
        </Text>
        <Badge
          color='green'
          size='md'
          variant='light'
          className={classes.statusBadge}
        >
          Successful
        </Badge>
      </div>

      <Divider my='md' />

      <div className={classes.resultDetails}>
        {result.paymentMethod === 'bankAccount' && (
          <Stack align='center' className={classes.detailRow}>
            <Text size='sm'>
              Thank you for your payment in the amount of{' '}
              {Number(result.amount).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              })}
              . Please print this authorization for your records.
            </Text>
            <Text size='sm'>
              You have authorized us to initiate an automated clearing house
              (ACH) one-time debit in your name to your bank account. This
              transaction will be presented to your financial institution by the
              next business day. You further agree that you may not revoke this
              authorization or cancel this payment.
            </Text>
          </Stack>
        )}
        <Flex align='center' className={classes.detailRow}>
          <ThemeIcon size='md' radius='xl' color='blue.5' variant='light'>
            <CreditCardIcon size={14} />
          </ThemeIcon>
          <Text fw={500} size='sm' className={classes.detailLabel}>
            Payment Method
          </Text>
          <div className={classes.detailValue}>
            {result.paymentMethod === 'card' ? (
              <Group gap='xs'>
                {cardLogoSrc && (
                  <img src={cardLogoSrc} alt='Card Logo' height={24} />
                )}
                <Text size='sm'>{`•••• ${result.paymentInstrument.lastFour}`}</Text>
              </Group>
            ) : (
              <Group gap='xs'>
                <Text size='sm'>eCheck</Text>
                {result.paymentInstrument.name && (
                  <Text size='xs' c='dimmed'>
                    ({result.paymentInstrument.name})
                  </Text>
                )}
              </Group>
            )}
          </div>
        </Flex>

        <Flex align='center' className={classes.detailRow}>
          <ThemeIcon size='md' radius='xl' color='violet.5' variant='light'>
            <IdCardIcon size={14} />
          </ThemeIcon>
          <Text fw={500} size='sm' className={classes.detailLabel}>
            Transaction ID
          </Text>
          <Text size='sm' ff='monospace' className={classes.detailValue}>
            {result.payFacTransactionId}
          </Text>
        </Flex>
      </div>

      <Divider my='md' />

      <div className={classes.resultFooter}>
        <Text c='dimmed' size='xs' ta='center'>
          A receipt has been sent to your email address
        </Text>
      </div>
    </>
  );
};

const getCardLogoSrc = (cardLogo: string) => {
  switch (cardLogo.toLowerCase()) {
    case 'visa':
      return visa;
    case 'mastercard':
      return mastercard;
    case 'amex':
      return amex;
    case 'discover':
      return discover;
  }
};

const TransactionCancelled = () => {
  return (
    <Stack align='center' gap='lg' p='xl'>
      <ThemeIcon size={64} radius={32} color='gray'>
        <XIcon size={32} />
      </ThemeIcon>
      <Stack gap={0} align='center'>
        <Title order={2}>Transaction Cancelled</Title>
        <Text c='dimmed' size='sm'>
          You have cancelled the payment process.
        </Text>
      </Stack>
    </Stack>
  );
};

const TransactionFailed = ({ recoverable }: { recoverable: boolean }) => {
  return (
    <>
      <div className={classes.resultHeader}>
        <ThemeIcon
          size={44}
          radius={44}
          color='red'
          className={classes.statusIcon}
        >
          <XIcon size={24} strokeWidth={2.5} />
        </ThemeIcon>
        <Title size='h4' className={classes.resultTitle}>
          Payment Failed
        </Title>
      </div>

      <div className={classes.centeredContent}>
        <Text size='sm' c='dimmed' ta='center' className={classes.messageText}>
          {recoverable
            ? 'There was an issue processing your payment. Please try again or contact support.'
            : 'There was an error processing your payment. Please try a different payment method or contact support.'}
        </Text>
        {!recoverable && (
          <Badge
            color='red'
            size='md'
            variant='light'
            mt='md'
            className={classes.statusBadge}
          >
            Payment Failed
          </Badge>
        )}

        {recoverable && (
          <button
            className={'primary-button'}
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        )}
      </div>
    </>
  );
};
